import React, { ReactNode, Component } from "react";
import { Col, Container, Row, Button, Tabs, Tab, Form, OverlayTrigger, Tooltip, Collapse, Modal, Alert } from "react-bootstrap";
import { RouteComponentProps, withRouter } from "react-router-dom";
import styled from "styled-components";
import Branding from "../config/Branding";
import { CentralCol, CarouselRow } from "../components/SharedStyled";

import { SearchResults } from "../components/SearchResults";
import ConditionalFiltersEditor from "../components/filters/ConditionalFiltersEditor";
import FactorDropdown from "../components/factors/FactorDropdown";
import FactorSlider from "../components/factors/FactorSlider";
import FactorFlag from "../components/factors/FactorFlag";
import FactorsRadar, { FactorsRadarAxis } from "../components/factors/FactorsRadar";
import { FilterUITypes } from "../enums";
import { previewSlot, getFiltersDomainInformation, getDynamicModelById, putDynamicModel, getHostURL } from "../utils/Requests";
import { buildChartPreviewPayload } from "../utils/ChartPreview";
import type { BuilderStateLike } from "../utils/ChartPreview";
import { ANY_VALUE } from "../constants";
import MetadataCarousel from "../components/MetadataCarousel";
import { Typeahead } from "react-bootstrap-typeahead";
import SaveDynamicModelModal from "../components/models/SaveDynamicModelModal";
import { DYNAMIC_MODEL_BASE_SLOT_ID } from "../constants";
import { generateGuid, extractParameterValue, isReadOnly as isReadOnlyUser } from "../utils/Utils";
import { Lock as LockIcon, Unlock as UnlockIcon, Copy as CopyIcon, ChevronDown, ChevronUp, ArrowCounterclockwise, ArrowLeft, ArrowRight } from "react-bootstrap-icons";
import copy from "copy-to-clipboard";


styled(Row)<{ standalone?: boolean }>`
    background: ${Branding.transparentBackground};
    top: ${(props) => (props.standalone ? "30px" : "63px")};
    margin-top: 10px;
`;


// Height of the viewport area available to the two panes (excludes the top app/header area).
const TwoPaneRow = styled(Row)<{ standalone?: boolean }>`
    /* The parent row that contains the two columns */
    height: calc(100vh - ${(p) => (p.standalone ? "30px" : "63px")} - 20px);
    overflow: visible; /* allow page to scroll if inner panes fail to overflow */
    align-items: stretch;
    @media (max-width: 767.98px) {
        height: auto;
        overflow: visible;
    }
`;

const LeftPaneScroll = styled.div`
    height: 100%;
    flex: 1 1 auto;
    min-height: 0;
    //overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* iOS momentum */
    padding: 20px; /* preserves existing spacing */
`;

const RightPaneScroll = styled.div`
    position: relative; /* establish stacking context for sticky header and children */
    height: 100%;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 20px;
`;

const RightStickyHeader = styled.div`
    position: sticky;
    top: 0;
    z-index: 1001; /* above result overlays (z=10) */
    background: ${Branding.transparentBackground};
    backdrop-filter: saturate(120%) blur(2px);
    margin-bottom: 8px;
    padding-bottom: 6px; /* space under the sticky row */
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
`;

const SmallTabs = styled(Tabs)`
    .nav-link {
        font-size: 13px;
        padding: 8px 12px;
    }
`;

const LeftCol = styled(Col)`
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0; /* allow nested scroll containers to work */
`;

const RightCol = styled(Col)`
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0; /* allow nested scroll containers to work */
`;


type HistorySnapshot = {
    factors: Record<string, string>;
    conditionalFilters: ConditionalFilters;
    fieldAffinities: FieldAffinity[];
};

type DynamicModelBuilderState = {
    alertMsg: string;
    alertType: string;

    userId?: string;
    seedId?: string;
    searchTerm?: string;
    // Conditional filters (replaces the old flat filters Record)
    conditionalFilters: ConditionalFilters;
    availableFilters: Array<DropdownFilter | RangeSliderFilter>;
    factors: Record<string, string>;
    availableFactors: Array<Factor>;
    // Available affinities from the API (field definitions with possible values)
    availableAffinities: Array<{ field: string; label: string; type: "categorical" | "datetime" | "age"; values: Array<{ id: string; label: string }>; newerIsProminent?: boolean; days?: number }>;
    // Field affinities configured for this slot (scoring boosts/penalties)
    fieldAffinities: FieldAffinity[];
    items: Array<Record<string, any>>;
    isLoading: boolean;

    isLoaded: boolean;
    error: Error | null;
    activeRightTab: "results" | "factors";
    // Left pane tab selection: Overview | Scoring | Filtering
    activeLeftTab: "overview" | "scoring" | "filtering";
    factorPreviewRows: Record<string, { title: string; items: any[]; isLoading: boolean; error?: string }>;
    factorPreviewSignature: string;

    selectedPlaylistDeletable: boolean;

    // Dynamic model specific fields
    modelId?: string;
    modelName: string;
    notes: string;
    showSaveModal: boolean;
    isSaving: boolean;
    isEditMode: boolean;

    // Baseline state for diff comparison (captured after initial load)
    baselineFactors: Record<string, string>;
    baselineConditionalFilters: ConditionalFilters;
    baselineFieldAffinities: FieldAffinity[];

    // Undo/Redo history
    history: Array<HistorySnapshot>;
    historyIndex: number;

    // UI state for diff panel and reset modal
    showDiffPanel: boolean;
    showResetConfirmModal: boolean;
};

type DynamicModelBuilderProps = RouteComponentProps & {
    groups: string[];
    standalone?: boolean;
    username: string;
    customerConfig: CustomerConfig;
};

class DynamicModelBuilder extends Component<DynamicModelBuilderProps, DynamicModelBuilderState> {
    // Guard to avoid duplicate/concurrent loads
    private isLoadingFilters = false;
    private previewDebounceTimer: NodeJS.Timeout | null = null;
    private resizeObserverErrorHandler: ((e: ErrorEvent) => void) | null = null;
    // True when the loaded model already had a conditional_filters field (vs. legacy parameters-only model)
    private modelHasConditionalFilters = false;

    constructor(props: DynamicModelBuilderProps) {
        super(props);
        this.state = {
            alertMsg: "",
            alertType: "success",

            userId: undefined,
            seedId: undefined,
            searchTerm: undefined,
            conditionalFilters: { operator: "AND", groups: [] },
            availableFilters: [],
            factors: {},
            availableFactors: [],
            availableAffinities: [],
            items: [],
            isLoading: false,
            isLoaded: false,
            error: null,
            activeRightTab: "results",
            activeLeftTab: "overview",

            factorPreviewRows: {},
            factorPreviewSignature: "",

            selectedPlaylistDeletable: true,

            // Start with no affinities until loaded from model or user adds them
            fieldAffinities: [],

            // Dynamic model specific fields
            modelId: undefined,
            modelName: "",
            notes: "",
            showSaveModal: false,
            isSaving: false,
            isEditMode: false,

            // Baseline state for diff comparison
            baselineFactors: {},
            baselineConditionalFilters: { operator: "AND", groups: [] },
            baselineFieldAffinities: [],

            // Undo/Redo history
            history: [],
            historyIndex: -1,

            // UI state for diff panel and reset modal
            showDiffPanel: false,
            showResetConfirmModal: false
        };

    }

    private toggleSelectedPlaylistDeletable = () => {
        this.setState((state) => ({ selectedPlaylistDeletable: !state.selectedPlaylistDeletable }));
    };


    public async componentDidMount(): Promise<void> {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const modelId = urlParams.get("modelId");

        if (modelId) {
            // Edit mode: load existing model
            this.setState({ modelId, isEditMode: true });
            await this.loadModel(modelId);
        }

        if (!this.isLoadingFilters) {
            this.isLoadingFilters = true;
            await this.loadFilters();
            this.isLoadingFilters = false;
        }

        // Suppress ResizeObserver errors that occur when tabbing away/back
        this.resizeObserverErrorHandler = (e: ErrorEvent) => {
            if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
                e.stopImmediatePropagation();
                return false;
            }
        };
        window.addEventListener("error", this.resizeObserverErrorHandler);
    }

    private async loadModel(modelId: string): Promise<void> {
        try {
            const model = await getDynamicModelById(this.props.customerConfig, modelId);

            /* eslint-disable camelcase */
            // Extract data from model
            const {
                playlist_name,
                variant,
                overlay,
                parameters,
                field_affinities,
                conditional_filters,
                notes,
                deletable
            } = model;
            /* eslint-enable camelcase */

            // Convert parameters to factors — all params go in initially;
            // loadFilters() will later remove any that correspond to filter qsParams.
            const factors: Record<string, string> = {};

            // Add variant and overlay to factors
            if (variant) factors.variant = variant;
            if (overlay) factors.overlay = overlay;

            // Add all other parameters to factors (will be sorted after loading filter definitions)
            Object.entries(parameters || {}).forEach(([key, value]) => {
                factors[key] = String(value);
            });

            /* eslint-disable camelcase */
            // Convert field_affinities to frontend format
            const fieldAffinities = (field_affinities || []).map((aff: any) => ({
                label: aff.label || aff.field,
                field: aff.field,
                values: aff.values || [],
                score: typeof aff.score === "number" ? aff.score : parseFloat(aff.score) || 1.0,
                type: aff.type,
                newerIsProminent: aff.newer_is_prominent,
                days: aff.days
            }));
            /* eslint-enable camelcase */

            // Parse conditional_filters if present (new models)
            /* eslint-disable camelcase */
            let conditionalFilters: ConditionalFilters = { operator: "AND", groups: [] };
            if (conditional_filters && Array.isArray(conditional_filters.groups)) {
                this.modelHasConditionalFilters = true;
                conditionalFilters = {
                    operator: (conditional_filters.operator === "OR" ? "OR" : "AND") as "AND" | "OR",
                    groups: conditional_filters.groups.map((g: any) => ({
                        operator: (g.operator === "OR" ? "OR" : "AND") as "AND" | "OR",
                        conditions: (g.conditions || []).map((c: any) => ({
                            topic: c.topic || "",
                            values: Array.isArray(c.values) ? c.values : [c.values || ""]
                        }))
                    }))
                };
            } else {
                // Old model: no conditional_filters; loadFilters() will convert parameters → conditionalFilters
                this.modelHasConditionalFilters = false;
            }
            /* eslint-enable camelcase */

            /* eslint-disable camelcase */
            this.setState({
                modelName: playlist_name,
                notes: notes || "",
                factors,
                fieldAffinities,
                conditionalFilters,
                selectedPlaylistDeletable: deletable !== undefined ? deletable : true
            });
            /* eslint-enable camelcase */
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to load model:", error);
            this.setState({
                alertMsg: "Failed to load model",
                alertType: "danger"
            });
        }
    }



    /**
     * Flatten ConditionalFilters into a Record<string, string> for use in URL query parameters.
     * All conditions across all groups are merged by topic (last write wins for duplicate topics).
     * NOTE: This loses AND/OR group logic — only use for URL display purposes.
     * For accurate filtering, pass the full conditionalFilters structure via BuilderStateLike.
     */
    private flattenConditionalFiltersForUrl(): Record<string, string> {
        const flat: Record<string, string> = {};
        this.state.conditionalFilters.groups.forEach(group => {
            group.conditions.forEach(condition => {
                const val = condition.values[0] || "";
                if (condition.topic && val !== "") {
                    flat[condition.topic] = val;
                }
            });
        });
        return flat;
    }

    private buildBuilderStateForPreview(extraFilters?: Record<string, string>, responseSize?: number): BuilderStateLike {
        const { factors, userId, seedId, searchTerm } = this.state;

        // Merge: factors < extraFilters (extraFilters win for factor preview overrides)
        const merged: Record<string, string> = { ...factors, ...(extraFilters || {}) };

        // Transform variant/overlay for preview requests.
        // Keep both forms: defaultVariant/defaultOverlay for the slot experiment parameters,
        // and variant/overlay so the backend can set them directly on the Request object
        // (matching how the slot+modelId path resolves them from the stored model config).
        const transformedMerged: Record<string, string> = {};
        Object.entries(merged).forEach(([k, v]) => {
            if (k === "variant") {
                transformedMerged["defaultVariant"] = v;
                transformedMerged["variant"] = v;
            } else if (k === "overlay") {
                transformedMerged["defaultOverlay"] = v;
                transformedMerged["overlay"] = v;
            } else if (
                (k === "maintopic" || k === "excludeMaintopic") &&
                v !== ""
            ) {
                const qsParam = v
                    .split(";")
                    .map(item => `"${item.replace(/"/g, "\\\"")}"`)
                    .join(",");

                transformedMerged[k] = qsParam;
            } else if (v !== "") {
                transformedMerged[k] = v;
            }
        });

        return {
            filters: transformedMerged,
            // Pass the full conditional filters structure for model-level injection
            conditionalFilters: this.state.conditionalFilters,
            // Surface current field affinities for preview; builder injects them under first model
            fieldAffinities: this.state.fieldAffinities || [],
            userId: userId?.trim(),
            seedId,
            searchTerm,
            responseSize
        } as BuilderStateLike;
    }

    private handleSave = (): void => {
        this.setState({ showSaveModal: true });
    }

    private async saveDynamicModel(): Promise<void> {
        const { customerConfig } = this.props;
        const {
            modelId,
            modelName,
            notes,
            factors,
            conditionalFilters,
            fieldAffinities
        } = this.state;

        if (!modelName.trim()) {
            this.setState({
                alertMsg: "Please enter a model name",
                alertType: "danger",
                showSaveModal: false
            });
            return;
        }

        this.setState({ isSaving: true });

        try {
            // Parameters contain only factor params (not filter params — those go in conditional_filters)
            const parameters: Record<string, string> = { ...factors };

            // Remove variant and overlay from parameters (they're top-level fields)
            // Extract the value part (e.g., "variant=7d" -> "7d")
            const variantRaw = parameters.variant || "variant=7d";
            const overlayRaw = parameters.overlay || "overlay=play";
            const variant = extractParameterValue(variantRaw);
            const overlay = extractParameterValue(overlayRaw);
            delete parameters.variant;
            delete parameters.overlay;

            // Convert field affinities to backend format
            // eslint-disable-next-line camelcase
            const field_affinities = fieldAffinities.map((aff: any) => ({
                field: aff.field,
                values: aff.values,
                score: aff.score,
                type: aff.type,
                // eslint-disable-next-line camelcase
                newer_is_prominent: aff.newerIsProminent,
                days: aff.days
            }));

            // Generate model ID if creating new model
            const finalModelId = modelId || generateGuid();

            // Build slot URL (only modelId needed - variant/overlay come from the model)
            const hostUrl = getHostURL(customerConfig);
            // eslint-disable-next-line camelcase
            const slot_url = `${hostUrl}/slots/${DYNAMIC_MODEL_BASE_SLOT_ID}/items?modelId=${finalModelId}`;

            // Build the conditional_filters payload: omit groups with no conditions
            /* eslint-disable camelcase */
            const conditional_filters = {
                operator: conditionalFilters.operator,
                groups: conditionalFilters.groups
                    .map(g => ({
                        operator: g.operator,
                        conditions: g.conditions.filter(c => c.topic && (c.values[0] || "") !== "")
                    }))
                    .filter(g => g.conditions.length > 0)
            };
            /* eslint-enable camelcase */

            const modelBody: any = {
                // eslint-disable-next-line camelcase
                model_id: finalModelId,
                // eslint-disable-next-line camelcase
                playlist_name: modelName,
                // eslint-disable-next-line camelcase
                model_type: "dynamic" as const,
                // eslint-disable-next-line camelcase
                slot_id: DYNAMIC_MODEL_BASE_SLOT_ID,
                // eslint-disable-next-line camelcase
                slot_url,
                variant,
                overlay,
                parameters,
                // eslint-disable-next-line camelcase
                field_affinities,
                // eslint-disable-next-line camelcase
                conditional_filters,
                status: "Live",
                deletable: this.state.selectedPlaylistDeletable
            };

            // Only include notes if it has content
            if (notes && notes.trim()) {
                modelBody.notes = notes.trim();
            }

            await putDynamicModel(
                customerConfig,
                modelBody.model_id,
                modelBody
            );

            this.setState({
                alertMsg: "Model saved successfully",
                alertType: "success",
                isSaving: false,
                showSaveModal: false
            });

            // Navigate back to Model Manager after short delay
            setTimeout(() => {
                this.props.history.push("/modelmanager");
            }, 1500);

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to save model:", error);
            this.setState({
                alertMsg: "Failed to save model",
                alertType: "danger",
                isSaving: false,
                showSaveModal: false
            });
        }
    }







    public componentWillUnmount(): void {
        // Clean up error handler
        if (this.resizeObserverErrorHandler) {
            window.removeEventListener("error", this.resizeObserverErrorHandler);
            this.resizeObserverErrorHandler = null;
        }

        // Clean up debounce timer
        if (this.previewDebounceTimer) {
            clearTimeout(this.previewDebounceTimer);
        }
    }

    private async loadFilters(): Promise<void> {
        const maxAttempts = 3;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response: any = await getFiltersDomainInformation(this.props.customerConfig);
                // Some backends return { filters: [...] }; support both shapes
                const filtersArray: Array<Record<string, any>> = Array.isArray(response)
                    ? response
                    : (Array.isArray(response?.filters) ? response.filters : []);

                // Extract factors from the response
                const factorsArray: Array<Factor> = Array.isArray(response?.factors) ? response.factors : [];

                // Extract affinities from the API response
                const affinitiesArray: Array<Record<string, any>> = Array.isArray(response?.affinities) ? response.affinities : [];

                // Initialize factor parameters with default values for all parameters
                const initialFactors: Record<string, string> = {};
                factorsArray.forEach(factor => {
                    if (factor.parameters && Array.isArray(factor.parameters)) {
                        factor.parameters.forEach(param => {
                            if (param.default !== undefined) {
                                initialFactors[param.name] = String(param.default);
                            }
                        });
                    }
                });

                const availableFilters: Array<DropdownFilter | RangeSliderFilter> = [];

                // Process available affinities from API
                const availableAffinities: Array<{ field: string; label: string; type: "categorical" | "datetime" | "age"; values: Array<{ id: string; label: string }>; newerIsProminent?: boolean; days?: number }> = [];
                affinitiesArray.forEach((affinity: Record<string, any>) => {
                    if (affinity.field && affinity.label) {
                        const affinityDef: any = {
                            field: affinity.field,
                            label: affinity.label,
                            type: affinity.type === "datetime" ? "datetime" : (affinity.type === "age" ? "age" : "categorical"),
                            values: Array.isArray(affinity.values) ? affinity.values.map((v: any) => ({
                                id: v.id ?? v.label ?? String(v),
                                label: v.label ?? v.id ?? String(v)
                            })) : []
                        };
                        // Convert newer_is_prominent from backend to newerIsProminent for frontend
                        if (affinity.newer_is_prominent !== undefined) {
                            affinityDef.newerIsProminent = affinity.newer_is_prominent;
                        } else if (affinity.newerIsProminent !== undefined) {
                            // Support old camelCase format too
                            affinityDef.newerIsProminent = affinity.newerIsProminent;
                        }
                        // Handle days parameter for age type
                        if (affinity.days !== undefined) {
                            affinityDef.days = affinity.days;
                        }
                        availableAffinities.push(affinityDef);
                    }
                });

                filtersArray.forEach((filter: Record<string, any>) => {
                    // Current API exposes topics with values: [{ id, label }, ...]
                    // Store as objects with id/label pairs so we can use IDs in state/API and labels in UI
                    // Searchable filters have filterType: "searchable" and empty values array
                    if (Array.isArray(filter.values) || filter.filterType === "searchable") {
                        const values: Array<{ id: string; label: string }> = (filter.values || []).map((v: any) => ({
                            id: v.id ?? v.label ?? String(v),
                            label: v.label ?? v.id ?? String(v)
                        }));
                        availableFilters.push({
                            id: filter.topic || filter.id || filter.label,
                            name: filter.label || filter.topic || "Filter",
                            type: FilterUITypes.Dropdown,
                            qsParam: filter.topic || filter.qsParam,
                            // Searchable filters don't have pre-populated values
                            values: filter.filterType === "searchable" ? [] : [ANY_VALUE, ...values],
                            defaultValue: ANY_VALUE,
                            cardinality: filter.cardinality,
                            fieldKey: filter.field || filter.fieldKey || (filter.topic || filter.qsParam),
                            // Preserve filterType for searchable filters
                            filterType: filter.filterType
                        });
                    }
                    // Range type not provided by this endpoint yet; keep for forward compatibility
                    if (filter.type === "range" && filter.qsMinParam && filter.qsMaxParam) {
                        availableFilters.push({
                            id: filter.id || `${filter.qsMinParam}-${filter.qsMaxParam}`,
                            name: filter.label || filter.name || "Range",
                            type: FilterUITypes.RangeSlider,
                            qsMinParam: filter.qsMinParam,
                            qsMaxParam: filter.qsMaxParam,
                            min: filter.min ?? 0,
                            max: filter.max ?? 100,
                            steps: filter.steps ?? 1,
                            marks: filter.marks ?? {},
                            showToolTip: filter.showToolTip ?? true,
                            useMarksAsValue: filter.useMarksAsValue ?? false
                        });
                    }
                });

                // Separate loaded parameters into pure factors vs. filter params.
                // When loading a saved model, all parameters start in `factors`.
                // After we know which qsParams are filter-params, we remove them from factors.
                const filterQsParams = new Set(availableFilters.map(f => (f as any).qsParam as string | undefined).filter(Boolean));
                const currentFactors = { ...initialFactors, ...this.state.factors };
                const separatedFactors: Record<string, string> = {};

                // Collect filter-param values keyed by topic (for legacy model migration)
                const legacyFilterParams: Record<string, string> = {};

                Object.entries(currentFactors).forEach(([key, value]) => {
                    if (filterQsParams.has(key)) {
                        // This key is a filter qsParam — never put it in factors
                        legacyFilterParams[key] = value;
                    } else {
                        separatedFactors[key] = value;
                    }
                });

                // Build conditionalFilters for the final state
                let finalConditionalFilters: ConditionalFilters = this.state.conditionalFilters;

                if (!this.modelHasConditionalFilters) {
                    // Legacy model: convert filter params to a single AND group
                    const conditions: FilterCondition[] = Object.entries(legacyFilterParams)
                        .filter(([, value]) => value && value !== "")
                        .map(([topic, value]) => ({ topic, values: [value] }));

                    finalConditionalFilters = {
                        operator: "AND",
                        groups: conditions.length > 0 ? [{ operator: "AND", conditions }] : []
                    };
                }
                // For new models (this.modelHasConditionalFilters === true), conditionalFilters is
                // already set by loadModel(); we just leave it untouched.

                // Re-resolve labels for affinities loaded from the backend, which stores
                // field/type/days/newerIsProminent but not the human-readable label.
                // Must run after availableAffinities is built (hence inside loadFilters).
                const normalizedAffinities = this.state.fieldAffinities.map(aff => {
                    if (availableAffinities.some(def => def.label === aff.label)) return aff;
                    const match =
                        availableAffinities.find(
                            def =>
                                def.field === aff.field &&
                                def.type === aff.type &&
                                def.newerIsProminent === aff.newerIsProminent &&
                                Number(def.days) === Number(aff.days)
                        ) ||
                        availableAffinities.find(def => def.field === aff.field && def.type === aff.type);
                    return match ? { ...aff, label: match.label } : aff;
                });

                // Single setState to update everything at once
                this.setState({
                    availableFilters,
                    availableFactors: factorsArray,
                    availableAffinities,
                    fieldAffinities: normalizedAffinities,
                    factors: separatedFactors,
                    conditionalFilters: finalConditionalFilters
                }, () => {
                    // Capture baseline state for diff comparison and initialize history
                    this.captureBaseline();
                    this.debouncedPreviewRequest();
                });

                return; // success
            } catch (e: any) {
                // Retry on early auth/session timing issues
                if (attempt < maxAttempts) {
                    await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
                    continue;
                }

                // Final failure: surface alert and log
                // eslint-disable-next-line no-console
                console.error("Failed to load /info/filters after retries", e);
                this.setState({
                    availableFilters: [],
                    availableAffinities: [],
                    alertMsg: "Could not load available filters. Please refresh or re-login.",
                    alertType: "danger"
                });
            }
        }
    }

    private setSeedId = (value: string): void => {
        this.setState({ seedId: value });
    };

    /**
     * Capture the current state as the baseline for diff comparison.
     * Called after loadFilters completes (handles both new and edit modes).
     */
    private captureBaseline = (): void => {
        const { factors, conditionalFilters, fieldAffinities } = this.state;
        const initialSnapshot: HistorySnapshot = {
            factors: { ...factors },
            conditionalFilters: JSON.parse(JSON.stringify(conditionalFilters)),
            fieldAffinities: JSON.parse(JSON.stringify(fieldAffinities))
        };
        this.setState({
            baselineFactors: { ...factors },
            baselineConditionalFilters: JSON.parse(JSON.stringify(conditionalFilters)),
            baselineFieldAffinities: JSON.parse(JSON.stringify(fieldAffinities)),
            history: [initialSnapshot],
            historyIndex: 0
        });
    };

    /**
     * Push a snapshot of the current state to the history stack.
     * Truncates any "forward" history if we're not at the end.
     */
    private captureHistorySnapshot = (): void => {
        const { factors, conditionalFilters, fieldAffinities, history, historyIndex } = this.state;
        const snapshot: HistorySnapshot = {
            factors: { ...factors },
            conditionalFilters: JSON.parse(JSON.stringify(conditionalFilters)),
            fieldAffinities: JSON.parse(JSON.stringify(fieldAffinities))
        };
        // Truncate forward history if we've undone some steps
        const truncatedHistory = history.slice(0, historyIndex + 1);
        truncatedHistory.push(snapshot);
        this.setState({
            history: truncatedHistory,
            historyIndex: truncatedHistory.length - 1
        });
    };

    private canUndo = (): boolean => {
        return this.state.historyIndex > 0;
    };

    private canRedo = (): boolean => {
        return this.state.historyIndex < this.state.history.length - 1;
    };

    private handleUndo = (): void => {
        if (!this.canUndo()) return;
        const newIndex = this.state.historyIndex - 1;
        const snapshot = this.state.history[newIndex];
        this.setState({
            factors: { ...snapshot.factors },
            conditionalFilters: JSON.parse(JSON.stringify(snapshot.conditionalFilters)),
            fieldAffinities: JSON.parse(JSON.stringify(snapshot.fieldAffinities)),
            historyIndex: newIndex
        }, () => this.debouncedPreviewRequest());
    };

    private handleRedo = (): void => {
        if (!this.canRedo()) return;
        const newIndex = this.state.historyIndex + 1;
        const snapshot = this.state.history[newIndex];
        this.setState({
            factors: { ...snapshot.factors },
            conditionalFilters: JSON.parse(JSON.stringify(snapshot.conditionalFilters)),
            fieldAffinities: JSON.parse(JSON.stringify(snapshot.fieldAffinities)),
            historyIndex: newIndex
        }, () => this.debouncedPreviewRequest());
    };

    private handleReset = (): void => {
        const { baselineFactors, baselineConditionalFilters, baselineFieldAffinities } = this.state;
        const resetSnapshot: HistorySnapshot = {
            factors: { ...baselineFactors },
            conditionalFilters: JSON.parse(JSON.stringify(baselineConditionalFilters)),
            fieldAffinities: JSON.parse(JSON.stringify(baselineFieldAffinities))
        };
        // Add reset as a new history entry so user can undo the reset
        const { history, historyIndex } = this.state;
        const truncatedHistory = history.slice(0, historyIndex + 1);
        truncatedHistory.push(resetSnapshot);
        this.setState({
            factors: { ...baselineFactors },
            conditionalFilters: JSON.parse(JSON.stringify(baselineConditionalFilters)),
            fieldAffinities: JSON.parse(JSON.stringify(baselineFieldAffinities)),
            history: truncatedHistory,
            historyIndex: truncatedHistory.length - 1,
            showResetConfirmModal: false
        }, () => this.debouncedPreviewRequest());
    };

    private toggleDiffPanel = (): void => {
        this.setState((prev) => ({ showDiffPanel: !prev.showDiffPanel }));
    };

    private getDefaultBaselines(): { conditionalFilters: ConditionalFilters; factors: Record<string, string>; fieldAffinities: FieldAffinity[] } {
        return { conditionalFilters: { operator: "AND", groups: [] }, factors: {}, fieldAffinities: [] };
    }

    /**
     * Build a slot URL with all parameters as query string (instead of modelId).
     * This allows users to see and copy the full parameterized URL.
     */
    private buildParameterizedSlotUrl(): string {
        const { customerConfig } = this.props;
        const { factors, fieldAffinities } = this.state;

        const hostUrl = getHostURL(customerConfig);
        const baseUrl = `${hostUrl}/slots/${DYNAMIC_MODEL_BASE_SLOT_ID}/items`;

        const params = new URLSearchParams();

        // Add modelId as the first parameter if available
        if (this.state.modelId) {
            params.set("modelId", this.state.modelId);
        }

        // Add variant and overlay
        const variantRaw = factors.variant || "variant=7d";
        const overlayRaw = factors.overlay || "overlay=play";
        const variant = extractParameterValue(variantRaw);
        const overlay = extractParameterValue(overlayRaw);
        params.set("defaultVariant", variant);
        params.set("defaultOverlay", overlay);

        // Add all other factors (excluding variant/overlay)
        Object.entries(factors).forEach(([key, value]) => {
            if (key !== "variant" && key !== "overlay" && value !== "") {
                params.set(key, value);
            }
        });

        // Add all filters (flattened from conditionalFilters for URL display)
        const flatFilters = this.flattenConditionalFiltersForUrl();
        Object.entries(flatFilters).forEach(([key, value]) => {
            if (value && value !== "" && value !== ANY_VALUE) {
                if ((key === "maintopic" || key === "excludeMaintopic") && value !== "") {
                    const qsParam = value
                        .split(";")
                        .map(item => `"${item.replace(/"/g, "\\\"")}"`)
                        .join(",");
                    params.set(key, qsParam);
                } else {
                    params.set(key, value);
                }
            }
        });

        // Add field affinities as JSON if any exist
        if (fieldAffinities && fieldAffinities.length > 0) {
            // Convert to backend format
            const affinitiesPayload = fieldAffinities.map((aff: any) => ({
                field: aff.field,
                values: aff.values,
                score: aff.score,
                type: aff.type,
                // eslint-disable-next-line camelcase
                newer_is_prominent: aff.newerIsProminent,
                days: aff.days
            }));
            params.set("fieldAffinities", JSON.stringify(affinitiesPayload));
        }

        // Add format=rtve for RTVE customer
        if (customerConfig.slug === "rtve") {
            params.set("format", "rtve");
        }

        return `${baseUrl}?${params.toString()}`;
    }

    private copySlotUrlToClipboard = (): void => {
        const url = this.buildParameterizedSlotUrl();
        copy(url);
        this.setState({
            alertMsg: "URL copied to clipboard",
            alertType: "success"
        });
        // Auto-dismiss after 3 seconds
        setTimeout(() => {
            this.setState({ alertMsg: "" });
        }, 3000);
    };

    private debouncedPreviewRequest = (): void => {
        // Clear existing timer
        if (this.previewDebounceTimer) {
            clearTimeout(this.previewDebounceTimer);
        }

        // Set new timer for 500ms delay
        this.previewDebounceTimer = setTimeout(() => {
            if (this.state.activeRightTab === "factors") {
                this.requestFactorPreviews();
            } else {
                this.requestDynamicChartItems();
            }
        }, 500);
    };

    private handleConditionalFiltersChange = (conditionalFilters: ConditionalFilters): void => {
        this.setState({ conditionalFilters }, () => {
            this.captureHistorySnapshot();
            this.debouncedPreviewRequest();
        });
    };

    private setFactorParam = (paramName: string, value: string): void => {
        this.setState((prev) => ({ factors: { ...prev.factors, [paramName]: value } }), () => {
            // Capture history after state change so redo works
            this.captureHistorySnapshot();
            // Auto-trigger preview when factors change (debounced)
            this.debouncedPreviewRequest();
        });
    };



    private buildFactorParameterComponent = (parameter: FactorParameter, factorName: string, paramIndex: number): ReactNode => {
        // Skip hidden parameters
        if (parameter.hidden) {
            return null;
        }

        const key = `factor-${factorName}-${parameter.name}-${paramIndex}`;

        switch (parameter.type) {
            case "string": {
                return (
                    <Row key={key} style={{ marginBottom: "15px" }} className="align-items-center">
                        <Col sm="4" className="d-flex align-items-center" style={{ textAlign: "left", paddingRight: "5px", fontSize: "14px", color: Branding.white }}>
                            {parameter.label || parameter.name}
                        </Col>
                        <Col sm="8" style={{ paddingLeft: "5px" }}>
                            <FactorDropdown parameter={parameter as FactorDropdownParameter} callback={this.setFactorParam} value={(this.state.factors[(parameter as FactorDropdownParameter).name] ?? (parameter as FactorDropdownParameter).default ?? "") as string} />
                        </Col>
                    </Row>
                );
            }
            case "slider": {
                return (
                    <Row key={key} style={{ marginBottom: "15px" }}>
                        <FactorSlider parameter={parameter as FactorSliderParameter} callback={this.setFactorParam} value={parseFloat(this.state.factors[(parameter as FactorSliderParameter).name] ?? ((parameter as FactorSliderParameter).default ?? (parameter as FactorSliderParameter).minimum).toString())} />
                    </Row>
                );
            }
            case "boolean": {
                return (
                    <Row key={key} style={{ marginBottom: "15px" }} className="align-items-center">
                        <Col sm="12">
                            <FactorFlag parameter={parameter as FactorFlagParameter} callback={this.setFactorParam} value={(this.state.factors[(parameter as FactorFlagParameter).name] ?? ((parameter as FactorFlagParameter).default ?? false)).toString() === "true"} />
                        </Col>
                    </Row>
                );
            }
            default:
                return null;
        }
    };

    private buildFactorComponent = (factor: Factor, factorIndex: number): ReactNode => {
        if (!factor.parameters || !Array.isArray(factor.parameters)) {
            return null;
        }

        const visibleParameters = factor.parameters.filter(param => !param.hidden);

        if (visibleParameters.length === 0) {
            return null;
        }

        return (
            <div key={`factor-group-${factorIndex}`} style={{ marginBottom: "25px" }}>
                {/* Factor group header - similar to filter styling */}
                <div style={{
                    marginBottom: "10px",
                    paddingBottom: "5px",
                    borderBottom: "1px solid #333",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: Branding.white
                }}>
                    {factor.name}
                    {factor.description && (
                        <div style={{
                            fontSize: "11px",
                            fontWeight: "normal",
                            color: "rgba(255,255,255,0.75)",
                            marginTop: "2px",
                            fontStyle: "italic"
                        }}>
                            {factor.description}
                        </div>
                    )}
                </div>
                {visibleParameters.map((param, paramIndex) =>
                    this.buildFactorParameterComponent(param, factor.name, paramIndex)
                )}
            </div>
        );
    };

    private buildFactors = (): ReactNode => {
        return this.state.availableFactors.map((factor, i) => this.buildFactorComponent(factor, i));
    };

    private getFactorsRadarAxes = (): FactorsRadarAxis[] => {
        const axes: FactorsRadarAxis[] = [];
        const { availableFactors, factors } = this.state;

        availableFactors.forEach((factor) => {
            (factor.parameters || []).forEach((param) => {
                if (param.type === "slider" && !param.hidden) {
                    const p = param as FactorSliderParameter;
                    const raw = factors[p.name];
                    let value: number = p.default ?? p.minimum;
                    if (raw !== undefined) {
                        const parsed = parseFloat(raw);
                        if (!isNaN(parsed)) value = parsed;
                    }
                    axes.push({
                        label: p.label || p.name,
                        value,
                        minimum: p.minimum,
                        maximum: p.maximum
                    });
                }
            });
        });

        return axes;
    };



    private getVisibleSliderParams(): Array<{ name: string; label: string; minimum: number; maximum: number }> {
        const params: Array<{ name: string; label: string; minimum: number; maximum: number }> = [];
        this.state.availableFactors.forEach((factor) => {
            (factor.parameters || []).forEach((param) => {
                if (param.type === "slider" && !param.hidden) {
                    const p = param as FactorSliderParameter;
                    params.push({
                        name: p.name,
                        label: p.label || p.name,
                        minimum: p.minimum,
                        maximum: p.maximum
                    });
                }
            });
        });
        return params;
    }


    private buildFactorOverridesFor(focused: { name: string; minimum: number; maximum: number }): Record<string, string> {
        // Start with all current factor params (includes hidden defaults captured on load)
        const overrides: Record<string, string> = { ...this.state.factors };

        // Get all visible slider parameters
        const visibleSliders = this.getVisibleSliderParams();

        // Zero out all visible slider parameters (set to minimum)
        visibleSliders.forEach(param => {
            overrides[param.name] = String(param.minimum);
        });

        // Set the focused parameter to its maximum value
        overrides[focused.name] = String(focused.maximum);

        // Ensure enableX flags are true; and overlay/variant are carried through explicitly
        Object.entries(this.state.factors).forEach(([k, v]) => {
            if (k.toLowerCase().startsWith("enable")) {
                overrides[k] = "true";
            }
            if (k === "overlay" || k === "variant") {
                overrides[k] = v;
            }
        });

        return overrides;
    }

    // Affinities Editor (Scoring tab)
    private renderAffinitiesEditor(): ReactNode {
        // Editor for Field Affinities per plan step 6
        // - Field selector (by label, not field key)
        // - Values multiselect (Typeahead) based on the selected field (hidden for datetime type)
        // - Score slider (0..2, step 0.05)
        // For phase 1: simple stacked list; add/remove with buttons

        // Use available affinities from API instead of deriving from filters
        // Each affinity config is uniquely identified by label (multiple configs can target same field)
        const distinctFields: Array<{
            label: string;
            field: string;
            type: "categorical" | "datetime" | "age";
            options: string[];
            newerIsProminent?: boolean;
            days?: number;
        }> = (this.state.availableAffinities || []).map(affinity => ({
            label: affinity.label,
            field: affinity.field,
            type: affinity.type,
            options: affinity.values.map(v => v.label),
            newerIsProminent: affinity.newerIsProminent,
            days: affinity.days
        }));

        const updateAffinity = (index: number, patch: Partial<FieldAffinity>) => {
            const copy = [...this.state.fieldAffinities];

            copy[index] = { ...copy[index], ...patch } as FieldAffinity;
            this.setState({ fieldAffinities: copy }, () => {
                this.captureHistorySnapshot();
                this.debouncedPreviewRequest();
            });
        };
        const removeAffinity = (index: number) => {
            const copy = [...this.state.fieldAffinities];
            copy.splice(index, 1);
            this.setState({ fieldAffinities: copy }, () => {
                this.captureHistorySnapshot();
                this.debouncedPreviewRequest();
            });
        };
        const addAffinity = () => {
            const firstField = distinctFields[0];
            if (!firstField) return;
            const newAffinity: FieldAffinity = {
                label: firstField.label,
                field: firstField.field,
                values: [],
                score: 1.0,
                type: firstField.type
            };
            // Include newerIsProminent if present
            if (firstField.newerIsProminent !== undefined) {
                newAffinity.newerIsProminent = firstField.newerIsProminent;
            }
            // Include days if present
            if (firstField.days !== undefined) {
                newAffinity.days = firstField.days;
            }
            const copy = [...this.state.fieldAffinities, newAffinity];
            this.setState({ fieldAffinities: copy }, () => {
                this.captureHistorySnapshot();
            });
        };

        return (
            <div style={{ marginTop: 10 }}>
                <h6 style={{ marginBottom: 10, color: Branding.white }}>Field Affinities</h6>
                {this.state.fieldAffinities.map((aff, idx) => {
                    // Find the affinity definition by label (unique identifier)
                    // Label is the key that distinguishes different configs for the same field
                    const selectedField = distinctFields.find(df => df.label === aff.label);
                    const options = (selectedField?.options || []).map(v => ({ label: v, value: v }));
                    const hideValues = aff.type === "datetime" || aff.type === "age";

                    return (
                        <div key={`aff-${idx}`} style={{ borderTop: "1px solid #333", paddingTop: 10, marginBottom: 10 }}>
                            <Row className="align-items-center" style={{ marginBottom: 8 }}>
                                <Col sm="4" style={{ color: Branding.white, fontSize: 13 }}>Field</Col>
                                <Col sm="8">
                                    <Form.Control as="select" size="sm" value={aff.label} onChange={(e: any) => {
                                        const selected = distinctFields.find(df => df.label === e.target.value);
                                        if (selected) {
                                            const update: Partial<FieldAffinity> = {
                                                label: selected.label,
                                                field: selected.field,
                                                type: selected.type,
                                                values: []
                                            };
                                            // Include newerIsProminent if present in the selected config
                                            if (selected.newerIsProminent !== undefined) {
                                                update.newerIsProminent = selected.newerIsProminent;
                                            }
                                            // Include days if present in the selected config
                                            if (selected.days !== undefined) {
                                                update.days = selected.days;
                                            }
                                            updateAffinity(idx, update);
                                        }
                                    }}>
                                        {distinctFields.map(df => (
                                            <option key={df.label} value={df.label}>{df.label}</option>
                                        ))}
                                    </Form.Control>
                                </Col>
                            </Row>
                            {!hideValues && (
                                <Row className="align-items-center" style={{ marginBottom: 8 }}>
                                    <Col sm="4" style={{ color: Branding.white, fontSize: 13 }}>Values</Col>
                                    <Col sm="8">
                                        <Typeahead
                                            id={`aff-values-${idx}`}
                                            multiple
                                            clearButton
                                            selected={(aff.values || []).map(v => ({ label: v, value: v }))}
                                            onChange={(sel: any[]) => updateAffinity(idx, { values: sel.map((o: any) => o.value || o) })}
                                            options={options}
                                            placeholder={selectedField ? `Select ${selectedField.label} …` : "Select values …"}
                                        />
                                    </Col>
                                </Row>
                            )}
                            <Row className="align-items-center" style={{ marginBottom: 8 }}>
                                <Col sm="4" style={{ color: Branding.white, fontSize: 13 }}>Score</Col>
                                <Col sm="6">
                                    <Form.Control
                                        type="range"
                                        min={0}
                                        max={2}
                                        step={0.05}
                                        value={typeof aff.score === "number" ? aff.score : parseFloat(aff.score) || 1.0}
                                        onChange={(e: any) => updateAffinity(idx, { score: parseFloat(e.target.value) })}
                                    />
                                </Col>
                                <Col sm="2" style={{ color: Branding.white, fontSize: 12, textAlign: "right" }}>
                                    {(typeof aff.score === "number" ? aff.score : parseFloat(aff.score) || 1.0).toFixed(2)}
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="12" style={{ textAlign: "right" }}>
                                    <Button variant="outline-danger" size="sm" onClick={() => removeAffinity(idx)}>Remove</Button>
                                </Col>
                            </Row>
                        </div>
                    );
                })}
                <div style={{ textAlign: "right" }}>
                    <Button size="sm" onClick={addAffinity}>Add Affinity</Button>
                </div>
            </div>
        );
    }

    private async requestFactorPreviews(): Promise<void> {
        const { customerConfig } = this.props;
        const { userId, seedId, searchTerm, conditionalFilters, factors, fieldAffinities } = this.state as any;

        // Include factors and affinities in signature so changes invalidate cache
        const signature = JSON.stringify({ conditionalFilters, factors, fieldAffinities, userId, seedId, searchTerm });
        if (signature === this.state.factorPreviewSignature && Object.keys(this.state.factorPreviewRows).length > 0) {
            return; // cache hit
        }

        const sliderParams = this.getVisibleSliderParams();
        const initialRows: Record<string, { title: string; items: any[]; isLoading: boolean; error?: string }> = {};
        sliderParams.forEach((p) => {
            initialRows[p.name] = { title: p.label, items: [], isLoading: true };
        });
        this.setState({ factorPreviewRows: initialRows, factorPreviewSignature: signature });

        const concurrency = 4;
        const queue = sliderParams.slice();
        const runNext = async (): Promise<void> => {
            const p = queue.shift();

            if (!p) {
                return;
            }

            try {
                const overrides = this.buildFactorOverridesFor(p);

                // Use hardcoded slot ID - buildChartPreviewPayload will create default experiments
                const payload = buildChartPreviewPayload({
                    slotId: DYNAMIC_MODEL_BASE_SLOT_ID,
                    builderState: this.buildBuilderStateForPreview({ ...overrides }, 10),
                    type: "DynamicChart"
                });
                const resp = await previewSlot(customerConfig, payload, userId, seedId, searchTerm);
                const json = await resp.json();
                this.setState((prev) => ({
                    factorPreviewRows: {
                        ...prev.factorPreviewRows,
                        [p.name]: { ...prev.factorPreviewRows[p.name], items: json.items || [], isLoading: false }
                    }
                }));
            } catch (e: any) {
                const message = e?.message || "Failed to load preview";
                this.setState((prev) => ({
                    factorPreviewRows: {
                        ...prev.factorPreviewRows,
                        [p.name]: { ...prev.factorPreviewRows[p.name], isLoading: false, error: message }
                    }
                }));
            } finally {
                await runNext();
            }
        };

        await Promise.all(new Array(Math.min(concurrency, queue.length)).fill(0).map(() => runNext()));
    }

    private async requestDynamicChartItems(): Promise<void> {
        const { customerConfig } = this.props;
        const { userId, seedId, searchTerm, factors } = this.state;

        this.setState({ isLoading: true, isLoaded: false, error: null });

        try {
            // Slider values are sent unscaled — the backend applies scaling.
            const payload = buildChartPreviewPayload({
                slotId: DYNAMIC_MODEL_BASE_SLOT_ID,
                builderState: this.buildBuilderStateForPreview({ ...factors }),
                type: "DynamicChart"
            });

            const resp = await previewSlot(
                customerConfig,
                payload,
                userId,
                seedId,
                searchTerm
            );
            const json = await resp.json();
            this.setState({ items: json.items || [], isLoaded: true });
        } catch (error: any) {
            this.setState({ error });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    // Overview tab: list of field affinities with up/down indicator (sorted by score desc)
    private renderAffinitiesSummaryList(): ReactNode {
        const { fieldAffinities } = this.state as any;
        const hasAffinities = Array.isArray(fieldAffinities) && fieldAffinities.length > 0;

        // Sort by score descending
        const rows = [...fieldAffinities].sort((a: FieldAffinity, b: FieldAffinity) => {
            const scoreA = typeof a.score === "number" ? a.score : parseFloat(a.score) || 1.0;
            const scoreB = typeof b.score === "number" ? b.score : parseFloat(b.score) || 1.0;
            return scoreB - scoreA;
        });

        return (
            <div style={{ marginTop: 12 }}>
                <div style={{ marginBottom: 10, color: Branding.white, textAlign: "left", fontWeight: 700, letterSpacing: 0.2, borderBottom: "1px solid #333", paddingBottom: 4 }}>Field Affinities</div>
                {!hasAffinities && (
                    <div style={{ color: "#999", fontStyle: "italic", fontSize: 12 }}>None</div>
                )}
                {hasAffinities && (
                    <div>
                        {rows.map((aff: FieldAffinity, idx: number) => {
                            // Label is now stored directly on the affinity
                            const label = aff.label;
                            const score = typeof aff.score === "number" ? aff.score : parseFloat(aff.score) || 1.0;
                            const isUp = score > 1;
                            const isDown = score < 1;
                            return (
                                <div key={`aff-list-${idx}`} style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 20px",
                                    columnGap: 8,
                                    padding: "8px 0",
                                    color: Branding.white,
                                    fontSize: 13,
                                    textAlign: "left"
                                }}>
                                    <div style={{ opacity: 0.9 }}>
                                        <div style={{ fontWeight: 600 }}>{label}</div>
                                        {aff.values && aff.values.length > 0 && (
                                            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                {aff.values.join(", ")}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {isUp && <span style={{ color: Branding.twentyfouriLime }}>↑</span>}
                                        {!isUp && isDown && <span style={{ color: Branding.twentyfouriRed }}>↓</span>}
                                        {!isUp && !isDown && <span style={{ color: Branding.twentyfouriIndigo }}>—</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Render a collapsible diff panel showing changes from baseline.
     * Shows original values on the left and current values on the right.
     * Hides enable* parameters from display.
     */
    private renderDiffPanel(): ReactNode {
        const {
            baselineFactors, baselineConditionalFilters, baselineFieldAffinities,
            factors, conditionalFilters, fieldAffinities, showDiffPanel
        } = this.state;

        // Keys to hide from diff display (case-insensitive match on enable*)
        const shouldHideKey = (key: string) => /^enable/i.test(key);

        // Transform factors to display format (map variant/overlay to their default* forms)
        const transformForDisplay = (factorObj: Record<string, string>): Record<string, string> => {
            const result: Record<string, string> = {};
            Object.entries(factorObj).forEach(([key, value]) => {
                if (shouldHideKey(key)) return;
                if (key === "variant") {
                    result["defaultVariant"] = extractParameterValue(value);
                } else if (key === "overlay") {
                    result["defaultOverlay"] = extractParameterValue(value);
                } else if (value !== "") {
                    result[key] = value;
                }
            });
            return result;
        };

        /** Serialize conditional filters for compact diff display */
        const serializeConditionalFilters = (cf: ConditionalFilters): string => {
            if (!cf || cf.groups.length === 0) return "";
            return cf.groups.map((g, gi) =>
                `[G${gi + 1} ${g.operator}] ` + g.conditions
                    .filter(c => c.topic && (c.values[0] || "") !== "")
                    .map(c => `${c.topic}=${c.values[0] || ""}`)
                    .join(` ${g.operator} `)
            ).join(" AND ");
        };

        // Serialize affinities for comparison
        const serializeAffinities = (affs: FieldAffinity[]): string => {
            if (!affs || affs.length === 0) return "";
            return affs.map(a => `${a.label}:${(a.values || []).join(",")}@${a.score}`).join("; ");
        };

        const originalFactors = transformForDisplay(baselineFactors);
        const currentFactors = transformForDisplay(factors);
        const originalFiltersStr = serializeConditionalFilters(baselineConditionalFilters);
        const currentFiltersStr = serializeConditionalFilters(conditionalFilters);
        const originalAffinities = serializeAffinities(baselineFieldAffinities);
        const currentAffinities = serializeAffinities(fieldAffinities);

        const allFactorKeys = new Set([...Object.keys(originalFactors), ...Object.keys(currentFactors)]);

        const factorDiffs: Array<{ key: string; original: string; current: string; changed: boolean }> = [];
        allFactorKeys.forEach(key => {
            const orig = originalFactors[key] || "";
            const curr = currentFactors[key] || "";
            factorDiffs.push({ key, original: orig, current: curr, changed: orig !== curr });
        });

        const filtersChanged = originalFiltersStr !== currentFiltersStr;
        const affinitiesChanged = originalAffinities !== currentAffinities;
        const hasChanges = factorDiffs.some(d => d.changed) || filtersChanged || affinitiesChanged;

        return (
            <div style={{ marginTop: 10 }}>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={this.toggleDiffPanel}
                    style={{ width: "100%", marginBottom: 8 }}
                >
                    {showDiffPanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {" "}Changes{hasChanges ? " *" : ""}
                </Button>
                <Collapse in={showDiffPanel}>
                    <div style={{ backgroundColor: "rgba(0,0,0,0.3)", borderRadius: 4, padding: 10, fontSize: 12, fontFamily: "monospace" }}>
                        <Row>
                            <Col xs={6} style={{ borderRight: "1px solid #444" }}>
                                <div style={{ fontWeight: 700, color: Branding.white, marginBottom: 6, textAlign: "center" }}>Original</div>
                                {factorDiffs.map(d => (
                                    <div key={`orig-f-${d.key}`} style={{ color: d.changed ? "#ff6b6b" : "#aaa", marginBottom: 2 }}>
                                        {d.key}: {d.original || <span style={{ fontStyle: "italic" }}>(none)</span>}
                                    </div>
                                ))}
                                {(originalFiltersStr || filtersChanged) && (
                                    <div style={{ color: filtersChanged ? "#ff6b6b" : "#aaa", marginTop: 4, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                                        filters: {originalFiltersStr || <span style={{ fontStyle: "italic" }}>(none)</span>}
                                    </div>
                                )}
                                {(originalAffinities || affinitiesChanged) && (
                                    <div style={{ color: affinitiesChanged ? "#ff6b6b" : "#aaa", marginTop: 4 }}>
                                        affinities: {originalAffinities || <span style={{ fontStyle: "italic" }}>(none)</span>}
                                    </div>
                                )}
                            </Col>
                            <Col xs={6}>
                                <div style={{ fontWeight: 700, color: Branding.white, marginBottom: 6, textAlign: "center" }}>Current</div>
                                {factorDiffs.map(d => (
                                    <div key={`curr-f-${d.key}`} style={{ color: d.changed ? Branding.twentyfouriLime : "#aaa", marginBottom: 2 }}>
                                        {d.key}: {d.current || <span style={{ fontStyle: "italic" }}>(none)</span>}
                                    </div>
                                ))}
                                {(currentFiltersStr || filtersChanged) && (
                                    <div style={{ color: filtersChanged ? Branding.twentyfouriLime : "#aaa", marginTop: 4, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                                        filters: {currentFiltersStr || <span style={{ fontStyle: "italic" }}>(none)</span>}
                                    </div>
                                )}
                                {(currentAffinities || affinitiesChanged) && (
                                    <div style={{ color: affinitiesChanged ? Branding.twentyfouriLime : "#aaa", marginTop: 4 }}>
                                        affinities: {currentAffinities || <span style={{ fontStyle: "italic" }}>(none)</span>}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Collapse>
            </div>
        );
    }

    /**
     * Render the reset confirmation modal.
     */
    private renderResetConfirmModal(): ReactNode {
        return (
            <Modal
                show={this.state.showResetConfirmModal}
                onHide={() => this.setState({ showResetConfirmModal: false })}
                centered
            >
                <Modal.Header closeButton style={{ backgroundColor: Branding.superDarkBackground, color: Branding.white }}>
                    <Modal.Title>Reset All Changes?</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: Branding.superDarkBackground, color: Branding.white }}>
                    <p>This will restore all scoring, filtering, and affinity settings to their original values.</p>
                    <p><b>This action can be undone using the Undo button.</b></p>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: Branding.superDarkBackground }}>
                    <Button variant="secondary" onClick={() => this.setState({ showResetConfirmModal: false })}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={this.handleReset}>
                        Reset
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    private displayAlert = (message: string, type = "success") => {
        this.setState({
            alertMsg: message,
            alertType: type
        });
    };

    private renderFiltersSummary(): ReactNode {
        const { conditionalFilters, availableFilters } = this.state;
        const filterNameMap: Record<string, string> = {};
        availableFilters.forEach(f => {
            if ((f as DropdownFilter).qsParam) {
                filterNameMap[(f as DropdownFilter).qsParam] = f.name;
            }
        });

        const activeGroups = conditionalFilters.groups.filter(g =>
            g.conditions.some(c => c.topic && (c.values[0] || "") !== "")
        );
        const hasFilters = activeGroups.length > 0;

        return (
            <div style={{ marginTop: 12 }}>
                <div style={{ marginBottom: 10, color: Branding.white, textAlign: "left", fontWeight: 700, letterSpacing: 0.2, borderBottom: "1px solid #333", paddingBottom: 4 }}>Filters</div>
                {!hasFilters && (
                    <div style={{ color: "#999", fontStyle: "italic", fontSize: 12 }}>None</div>
                )}
                {hasFilters && activeGroups.map((group, gi) => {
                    const activeConditions = group.conditions.filter(c => c.topic && (c.values[0] || "") !== "");
                    return (
                        <div key={`grp-sum-${gi}`} style={{ marginBottom: 8 }}>
                            {/* Group header */}
                            <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>
                                {gi > 0 && <span style={{ color: Branding.twentyfouriIndigo, fontWeight: 700, marginRight: 4 }}>AND</span>}
                                Group {gi + 1}
                                <span style={{ marginLeft: 4, color: Branding.twentyfouriLime, fontWeight: 600 }}>({group.operator})</span>
                            </div>
                            {activeConditions.map((c, ci) => {
                                const label = filterNameMap[c.topic] || c.topic;
                                const values = (c.values[0] || "").split(",").filter(Boolean);
                                return (
                                    <div key={`cond-sum-${gi}-${ci}`} style={{
                                        paddingLeft: 8,
                                        paddingTop: 3,
                                        paddingBottom: 3,
                                        color: Branding.white,
                                        fontSize: 12,
                                        textAlign: "left"
                                    }}>
                                        <span style={{ fontWeight: 600 }}>{label}:</span>{" "}
                                        <span style={{ opacity: 0.8, whiteSpace: "normal", wordBreak: "break-word" }}>{values.join(", ")}</span>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        );
    }


    public render(): ReactNode {
        const { customerConfig, username, groups, standalone } = this.props;
        const isReadOnly = isReadOnlyUser(groups, customerConfig);
        const radarAxes = this.getFactorsRadarAxes();

        return (
            <Container className="mw-100">
                {this.state.alertMsg && (
                    <Alert
                        variant={this.state.alertType as any}
                        onClose={() => this.setState({ alertMsg: "" })}
                        dismissible
                        style={{ position: "fixed", top: 70, right: 20, zIndex: 9999, minWidth: 250 }}
                    >
                        {this.state.alertMsg}
                    </Alert>
                )}

                <Row className="justify-content-center">
                    <CentralCol md>
                        <TwoPaneRow standalone={standalone}>
                            <LeftCol md={3}>
                                <LeftPaneScroll>
                                    {/* Left Pane Tabs: Overview | Scoring | Filtering */}
                                    <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8 }}>
                                        <div style={{ flex: 1 }}>
                                            <SmallTabs id="dynamicmodelbuilder-left-tabs" activeKey={this.state.activeLeftTab} onSelect={(k: any) => {
                                                if (k === "lock") {
                                                    this.toggleSelectedPlaylistDeletable();
                                                    return; // Nepřepínat tab, jen toggle stav
                                                }
                                                this.setState({ activeLeftTab: (k as any) || "overview" });
                                            }}>
                                                <Tab eventKey="overview" title="Overview">
                                                    <Row style={{ marginTop: "5px", marginBottom: "10px" }}>
                                                        <Col lg style={{ textAlign: "left" }}>
                                                            {!isReadOnly && (
                                                                <div className="d-flex w-100 mt-2 mb-2">
                                                                    <Button className="flex-fill mr-2" disabled={this.state.isSaving} onClick={this.handleSave} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                                                                        {this.state.isSaving ? "Saving..." : (this.state.isEditMode ? `Update '${this.state.modelName}'` : "Save Model")}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                            <div className="d-flex w-100 mb-2">
                                                                <Button className="flex-fill mr-2" variant="secondary" onClick={() => this.props.history.push("/modelmanager")}>
                                                                    Return To Model Manager
                                                                </Button>
                                                            </div>
                                                            {/* Undo/Redo/Reset buttons */}
                                                            <div className="d-flex w-100 mb-2" style={{ gap: 6 }}>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="undo-tooltip">Undo</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={!this.canUndo()}
                                                                        onClick={this.handleUndo}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowLeft size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="redo-tooltip">Redo</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={!this.canRedo()}
                                                                        onClick={this.handleRedo}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowRight size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="reset-tooltip">Reset to original</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        onClick={() => this.setState({ showResetConfirmModal: true })}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowCounterclockwise size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger 
                                                                    placement="right" 
                                                                    overlay={
                                                                        <Tooltip id="copy-url-tooltip" style={{ maxWidth: 400 }}>
                                                                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Copy Slot URL</div>
                                                                            <div style={{ fontSize: 10, wordBreak: "break-all", fontFamily: "monospace" }}>
                                                                                {this.buildParameterizedSlotUrl()}
                                                                            </div>
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        onClick={this.copySlotUrlToClipboard}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <CopyIcon size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            </div>
                                                            {/* Collapsible diff panel */}
                                                            {this.renderDiffPanel()}
                                                        </Col>
                                                    </Row>

                                                    <Row style={{ marginTop: "10px", marginBottom: "20px" }}>
                                                        <Col lg>
                                                            <Form.Group>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={4}
                                                                    placeholder="Enter any notes about the model here..."
                                                                    value={this.state.notes}
                                                                    onChange={(e) => this.setState({ notes: e.target.value })}
                                                                    style={{
                                                                        backgroundColor: Branding.black,
                                                                        color: Branding.white,
                                                                        border: `1px solid ${Branding.twentyfouriIndigo}`,
                                                                        resize: "vertical"
                                                                    }}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>

                                                    <h6 style={{ marginBottom: "15px", marginTop: "6px", color: Branding.white }}>Factors</h6>
                                                    {radarAxes.length > 0 && (
                                                        <Row>
                                                            <Col md>
                                                                <div style={{ marginBottom: "5px" }}>
                                                                    <FactorsRadar axes={radarAxes} />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    )}

                                                    {this.renderAffinitiesSummaryList()}
                                                    {this.renderFiltersSummary()}
                                                </Tab>

                                                <Tab eventKey="scoring" title="Scoring">
                                                    <Row style={{ marginTop: "5px", marginBottom: "10px" }}>
                                                        <Col md>
                                                            {!isReadOnly && (
                                                                <div className="d-flex w-100 mt-2 mb-2">
                                                                    <Button className="flex-fill mr-2" disabled={this.state.isSaving} onClick={this.handleSave} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                                                                        {this.state.isSaving ? "Saving..." : (this.state.isEditMode ? `Update '${this.state.modelName}'` : "Save Model")}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                            <div className="d-flex w-100 mb-2">
                                                                <Button className="flex-fill mr-2" variant="secondary" onClick={() => this.props.history.push("/modelmanager")}>
                                                                    Return To Model Manager
                                                                </Button>
                                                            </div>
                                                            {/* Undo/Redo/Reset buttons */}
                                                            <div className="d-flex w-100 mb-2" style={{ gap: 6 }}>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="undo-tooltip-scoring">Undo</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={!this.canUndo()}
                                                                        onClick={this.handleUndo}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowLeft size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="redo-tooltip-scoring">Redo</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={!this.canRedo()}
                                                                        onClick={this.handleRedo}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowRight size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="reset-tooltip-scoring">Reset to original</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        onClick={() => this.setState({ showResetConfirmModal: true })}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowCounterclockwise size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger 
                                                                    placement="right" 
                                                                    overlay={
                                                                        <Tooltip id="copy-url-tooltip-scoring" style={{ maxWidth: 400 }}>
                                                                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Copy Slot URL</div>
                                                                            <div style={{ fontSize: 10, wordBreak: "break-all", fontFamily: "monospace" }}>
                                                                                {this.buildParameterizedSlotUrl()}
                                                                            </div>
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        onClick={this.copySlotUrlToClipboard}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <CopyIcon size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            </div>
                                                            {/* Collapsible diff panel */}
                                                            {this.renderDiffPanel()}
                                                        </Col>
                                                    </Row>
                                                    {this.state.availableFactors.length > 0 && (
                                                        <Row>
                                                            <Col md>
                                                                <div style={{ marginBottom: "20px" }}>
                                                                    {this.buildFactors()}
                                                                    {this.renderAffinitiesEditor()}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    )}
                                                </Tab>

                                                <Tab eventKey="filtering" title="Filtering">
                                                    <Row style={{ marginTop: "5px", marginBottom: "10px" }}>
                                                        <Col md>
                                                            {!isReadOnly && (
                                                                <div className="d-flex w-100 mt-2 mb-2">
                                                                    <Button className="flex-fill mr-2" disabled={this.state.isSaving} onClick={this.handleSave} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                                                                        {this.state.isSaving ? "Saving..." : (this.state.isEditMode ? `Update '${this.state.modelName}'` : "Save Model")}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                            <div className="d-flex w-100 mb-2">
                                                                <Button className="flex-fill mr-2" variant="secondary" onClick={() => this.props.history.push("/modelmanager")}>
                                                                    Return To Model Manager
                                                                </Button>
                                                            </div>
                                                            {/* Undo/Redo/Reset buttons */}
                                                            <div className="d-flex w-100 mb-2" style={{ gap: 6 }}>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="undo-tooltip-filtering">Undo</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={!this.canUndo()}
                                                                        onClick={this.handleUndo}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowLeft size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="redo-tooltip-filtering">Redo</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={!this.canRedo()}
                                                                        onClick={this.handleRedo}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowRight size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger placement="top" overlay={<Tooltip id="reset-tooltip-filtering">Reset to original</Tooltip>}>
                                                                    <Button
                                                                        variant="outline-warning"
                                                                        size="sm"
                                                                        onClick={() => this.setState({ showResetConfirmModal: true })}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <ArrowCounterclockwise size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger 
                                                                    placement="right" 
                                                                    overlay={
                                                                        <Tooltip id="copy-url-tooltip-filtering" style={{ maxWidth: 400 }}>
                                                                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Copy Slot URL</div>
                                                                            <div style={{ fontSize: 10, wordBreak: "break-all", fontFamily: "monospace" }}>
                                                                                {this.buildParameterizedSlotUrl()}
                                                                            </div>
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        onClick={this.copySlotUrlToClipboard}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <CopyIcon size={14} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            </div>
                                                            {/* Collapsible diff panel */}
                                                            {this.renderDiffPanel()}
                                                        </Col>
                                                    </Row>

                                                    <Row style={{ marginTop: "15px" }}>
                                                        <Col md>
                                                            <ConditionalFiltersEditor
                                                                conditionalFilters={this.state.conditionalFilters}
                                                                availableFilters={this.state.availableFilters.filter(f => f.type !== "RangeSlider") as DropdownFilter[]}
                                                                customerConfig={this.props.customerConfig}
                                                                onChange={this.handleConditionalFiltersChange}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Tab>

                                                <Tab 
                                                    eventKey="lock" 
                                                    title={
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id="lock-tab-tooltip">{this.state.selectedPlaylistDeletable ? "Click to lock (protect from deletion)" : "Click to unlock (allow deletion)"}</Tooltip>}
                                                        >
                                                            <span style={{ cursor: "pointer" }}>
                                                                {this.state.selectedPlaylistDeletable ? <UnlockIcon /> : <LockIcon />}
                                                            </span>
                                                        </OverlayTrigger>
                                                    }
                                                >
                                                    {/* Prázdný obsah - tab se používá jen jako toggle button */}
                                                </Tab>

                                            </SmallTabs>
                                        </div>
                                    </div>
                                </LeftPaneScroll>
                            </LeftCol>
                            <RightCol md={9}>
                                <RightPaneScroll>
                                    <RightStickyHeader>
                                        <Row>
                                            <Col md={12}>
                                                <SmallTabs id="dynamicmodelbuilder-results-factors-tabs" activeKey={this.state.activeRightTab} onSelect={(k: any) => {
                                                    const key = (k as string) || "results";
                                                    this.setState({ activeRightTab: key as any }, () => {
                                                        if (key === "factors") {
                                                            this.requestFactorPreviews();
                                                        } else if (key === "results") {
                                                            this.requestDynamicChartItems();
                                                        }
                                                    });
                                                }}>
                                                    <Tab eventKey="results" title="Results" />
                                                    <Tab eventKey="factors" title="Factors" />
                                                </SmallTabs>
                                            </Col>
                                        </Row>
                                    </RightStickyHeader>
                                    {this.state.activeRightTab === "results" && (
                                        <Row style={{ marginTop: "15px" }}>
                                            <Col md>
                                                {this.state.error && (
                                                    <Row className="justify-content-center">Error — {this.state.error.message}</Row>
                                                )}
                                                {this.state.isLoading && (
                                                    <Row className="justify-content-center"><div className="spinner-border text-primary" role="status" /></Row>
                                                )}
                                                {!this.state.isLoading && !this.state.isLoaded && !this.state.error && (
                                                    <Row className="justify-content-center">Results will appear here</Row>
                                                )}
                                                {!this.state.isLoading && this.state.isLoaded && !this.state.error && (
                                                    <SearchResults
                                                        customer={customerConfig}
                                                        items={this.state.items}
                                                        username={username}
                                                        groups={groups}
                                                        showMetadataLink
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    )}

                                    {this.state.activeRightTab === "factors" && (
                                        <>
                                            {Object.keys(this.state.factorPreviewRows).length === 0 && (
                                                <Row className="justify-content-center">Select the Factors tab to load previews…</Row>
                                            )}
                                            {(() => {
                                                const ranges = this.getVisibleSliderParams().reduce((acc: Record<string, { minimum: number; maximum: number }>, p) => {
                                                    acc[p.name] = { minimum: p.minimum, maximum: p.maximum };
                                                    return acc;
                                                }, {} as Record<string, { minimum: number; maximum: number }>);

                                                const norm = (name: string): number => {
                                                    const r = ranges[name];
                                                    const min = r?.minimum ?? 0;
                                                    const max = r?.maximum ?? 1;
                                                    const rawStr = this.state.factors[name];
                                                    const raw = rawStr !== undefined ? parseFloat(rawStr) : min;
                                                    const val = isNaN(raw) ? min : raw;
                                                    const range = Math.max(1e-9, max - min);
                                                    return (val - min) / range; // 0..1
                                                };

                                                return Object.entries(this.state.factorPreviewRows)
                                                    .sort(([aName], [bName]) => norm(bName) - norm(aName))
                                                    .map(([key, row]) => (
                                                        <CarouselRow key={key}>
                                                            <Col md style={{ textAlign: "left" }}>
                                                                <h5 style={{ paddingLeft: "40px" }} className="mx-auto">{row.title}</h5>
                                                                {row.isLoading && (
                                                                    <Row className="justify-content-center"><div className="spinner-border text-primary" role="status" /></Row>
                                                                )}
                                                                {!row.isLoading && row.error && (
                                                                    <Row className="justify-content-center">Error — {row.error}</Row>
                                                                )}
                                                                {!row.isLoading && !row.error && (
                                                                    <MetadataCarousel
                                                                        items={row.items}
                                                                        customer={customerConfig}
                                                                        hideInfoIcon
                                                                        modal
                                                                        username={username}
                                                                        groups={groups}
                                                                    />
                                                                )}
                                                            </Col>
                                                        </CarouselRow>
                                                    ));
                                            })()}


                                        </>
                                    )}

                <SaveDynamicModelModal
                    show={this.state.showSaveModal}
                    modelName={this.state.modelName}
                    isEdit={this.state.isEditMode}
                    onClose={() => this.setState({ showSaveModal: false })}
                    onSave={() => this.saveDynamicModel()}
                    onNameChange={(name: string) => this.setState({ modelName: name })}
                />

                {this.renderResetConfirmModal()}

                                </RightPaneScroll>
                            </RightCol>
                        </TwoPaneRow>
                    </CentralCol>
                </Row>
            </Container>
        );
    }
}

export default withRouter(DynamicModelBuilder);
